import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguageContext } from '@/i18n/LanguageContext';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import HeroSection from './components/HeroSection';
import CourseOverview from './components/CourseOverview';
import TargetAudience from './components/TargetAudience';
import LearningContent from './components/LearningContent';
import LearningProgression from './components/LearningProgression';
import DailySchedule from './components/DailySchedule';
import CertificationSection from './components/CertificationSection';
import CourseBenefits from './components/CourseBenefits';
import NextStep from './components/NextStep';
import AdmissionFlow from './components/AdmissionFlow';
import CTASection from './components/CTASection';
import RelatedPages from './components/RelatedPages';
import FAQSection from './components/FAQSection';
import Seo from '@/components/seo/Seo';

export default function EssentialCoursePage() {
  const { t } = useTranslation();
  const { lang } = useLanguageContext();
  const [showBackToTop, setShowBackToTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 600);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const isEn = lang === 'en';
  const baseUrl = isEn ? 'https://butler-academy.jp/en' : 'https://butler-academy.jp';

  const breadcrumbData = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: t('coursesEssential.jsonld.breadcrumb.home'),
        item: `${baseUrl}/`,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: t('coursesEssential.jsonld.breadcrumb.courses'),
        item: `${baseUrl}/courses`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: t('coursesEssential.jsonld.breadcrumb.current'),
        item: `${baseUrl}/courses/essential`,
      },
    ],
  };

  const courseData = {
    '@context': 'https://schema.org',
    '@type': 'Course',
    name: t('coursesEssential.jsonld.course.name'),
    description: t('coursesEssential.jsonld.course.description'),
    provider: {
      '@type': 'EducationalOrganization',
      name: t('coursesEssential.jsonld.provider'),
    },
    offers: {
      '@type': 'Offer',
      price: '780000',
      priceCurrency: 'JPY',
      category: 'Paid',
    },
    timeRequired: 'P1M',
  };

  return (
    <>
      <Seo route="/courses/essential" />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbData) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(courseData) }}
      />

      <Header />

      <main>
        <HeroSection />
        <CourseOverview />
        <TargetAudience />
        <LearningContent />
        <LearningProgression />
        <DailySchedule />
        <CertificationSection />
        <CourseBenefits />
        <NextStep />
        <AdmissionFlow />
        <CTASection />
        <RelatedPages />
        <FAQSection />

        {/* Last Updated */}
        <section className="py-8 bg-background-50">
          <div className="max-w-[1600px] mx-auto px-8 md:px-12 lg:px-16 text-center">
            {String(t('coursesEssential.lastUpdated')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-[13px] text-foreground-500 tracking-[0.05em]">{__s}</p>))}
          </div>
        </section>
      </main>

      <Footer />

      {/* Back to Top Button */}
      <button
        onClick={scrollToTop}
        className={`fixed bottom-8 right-8 z-40 w-11 h-11 rounded-full border border-accent-500/40 bg-primary-900/80 backdrop-blur-sm text-accent-400 hover:text-accent-300 hover:border-accent-400 hover:bg-primary-900 transition-all duration-300 flex items-center justify-center ${
          showBackToTop ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'
        }`}
        aria-label={t('common.backToTop')}
      >
        <i className="ri-arrow-up-line text-lg"></i>
      </button>
    </>
  );
}